import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import SwitchInput from '../../../../../components/SwitchInput';
import { THEME_COLOR, THEME_COLOR3, THEME_COLOR4 } from '../../../../../constants';
import type { TAvailabilityTiming } from '../schemas/menu-item-schema';

type TDayTimeslotCardProps = {
  label: string;
  enabled: boolean;
  timings: TAvailabilityTiming[];
  onToggle: (enabled: boolean) => void;
  onTimingChange: (index: number, patch: Partial<TAvailabilityTiming>) => void;
  onTimingAdd: () => void;
  onTimingRemove: (index: number) => void;
  dayError?: string;
  timingErrors?: Array<{ from?: string; to?: string }>;
};

const DayTimeslotCard = ({
  label,
  enabled,
  timings,
  onToggle,
  onTimingChange,
  onTimingAdd,
  onTimingRemove,
  dayError,
  timingErrors = [],
}: TDayTimeslotCardProps) => (
  <Box sx={s.card}>
    {/* Day row */}
    <Box sx={s.dayRow}>
      <SwitchInput checked={enabled} onChange={() => onToggle(!enabled)} />
      <Typography sx={s.dayLabel}>{label}</Typography>
      {!enabled && (
        <Typography sx={s.closedLabel}>Closed</Typography>
      )}
      {enabled && (
        <IconButton
          size="small"
          onClick={onTimingAdd}
          sx={s.addBtn}
          aria-label={`Add time slot for ${label}`}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>

    {/* Time slots */}
    {enabled && (
      <Box sx={s.timingsWrapper}>
        {timings.map((timing, index) => (
          <Box key={index} sx={s.timingRow}>
            <TextField
              size="small"
              type="time"
              label="From"
              value={timing.from}
              onChange={(e) => onTimingChange(index, { from: e.target.value })}
              InputLabelProps={{ shrink: true }}
              error={Boolean(timingErrors[index]?.from)}
              helperText={timingErrors[index]?.from}
              sx={s.timeField}
            />
            <Typography sx={s.timeSep}>–</Typography>
            <TextField
              size="small"
              type="time"
              label="To"
              value={timing.to}
              onChange={(e) => onTimingChange(index, { to: e.target.value })}
              InputLabelProps={{ shrink: true }}
              error={Boolean(timingErrors[index]?.to)}
              helperText={timingErrors[index]?.to}
              sx={s.timeField}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => onTimingRemove(index)}
              disabled={timings.length <= 1}
              aria-label="Remove time slot"
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
        {dayError && (
          <Typography sx={s.errorText}>{dayError}</Typography>
        )}
      </Box>
    )}
  </Box>
);

export default DayTimeslotCard;

const s = {
  card: {
    borderRadius: '12px',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FFF',
    overflow: 'hidden',
    mb: 1,
  },
  dayRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1.25,
  },
  dayLabel: {
    flex: 1,
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    color: '#000',
    textTransform: 'capitalize' as const,
  },
  closedLabel: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: THEME_COLOR3,
  },
  addBtn: {
    width: 28,
    height: 28,
    border: `1px dashed ${THEME_COLOR4}`,
    borderRadius: '8px',
    color: THEME_COLOR3,
    '&:hover': { borderColor: THEME_COLOR, color: THEME_COLOR },
  },
  timingsWrapper: {
    borderTop: `1px solid ${THEME_COLOR4}`,
    px: 2,
    py: 1.5,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
  },
  timingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  timeSep: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: THEME_COLOR3,
  },
  timeField: {
    width: 130,
    '& .MuiInputBase-root': { borderRadius: '8px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR4 },
  },
  errorText: {
    fontFamily: 'GilroyRegular',
    fontSize: 11,
    color: '#d32f2f',
    mt: 0.5,
  },
};
